/**
 * MembersTab — the People > Members surface.
 *
 * iter95-fix: the previous layout crammed avatar + name + phone + admin pill +
 * balance chip + book icon + remind icon + Make/Remove Admin button + Remove button
 * into a single horizontal row. On 390px viewports the balance chip wrapped to two
 * lines, button labels split, and rows looked broken.
 *
 * New layout:
 *   ┌──────────────────────────────────────────────────────┐
 *   │ avatar  Name                            balance chip │   ← primary row
 *   │         9876543210  (admin pill)        ledger book  │
 *   ├──────────────────────────────────────────────────────┤
 *   │   [Make Admin]  [Remind]  [Remove]   (admin only)    │   ← actions row
 *   └──────────────────────────────────────────────────────┘
 *
 * Audit Log / Reconcile / Invite Link moved out — they're now reachable from
 * the group ⋮ menu sheet (Wave 1) which is the right home for occasional
 * navigation, not constant action.
 */
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Send, MessageCircle, UserPlus, UserMinus, BookOpen, ShieldPlus, ShieldOff, X, ArrowLeftRight, FileText, MoreVertical, Coins, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import Avatar from '../../components/ui/avatar-initials';
import { GuestRow } from '../../components/GuestRow';
import NeoPayProofLink from '../../components/NeoPayProofLink';
import BalanceChip from '../../components/BalanceChip';
import ExplainBalanceSheet from '../../components/ExplainBalanceSheet';
import { waInviteUrl } from '../../components/PendingInvitesDialog';
import api from '../../lib/api';
import { sendSystemWaInvite } from '../../lib/systemWaInvite';

// iter239 — Digest options: text summary alone, or with each member's ledger PDF.
const DIGEST_OPTIONS = [
  { id: 'none', label: 'Summary only', sub: 'Balance, cash box, pending items — as a WhatsApp text' },
  { id: 'week', label: 'Summary + weekly ledger PDF', sub: "Each member also gets their own last-7-days ledger as a PDF" },
  { id: 'month', label: 'Summary + monthly ledger PDF', sub: "Each member also gets their own last-30-days ledger as a PDF" },
];

export default function MembersTab({ ctx }) {
  const {
    group,
    user,
    isAdmin,
    fundData,
    sendingGroupDigest,
    sendingReminder,
    handleSendGroupDigest,
    handleSendReminder,
    handleToggleGroupAdmin,
    handleRemoveMember,
    handleAddMemberBack,
    setShowAddMember,
    setNewMemberPhone,  // iter247 — prefill Add Member dialog from pending invites
    setShowInviteModal,
    setInviteCode,
    fetchGroup,
    fetchFund,
    openMemberLedger,
    openPairLedger,     // iter222 — pair-wise ledger
  } = ctx;

  // iter239 — "what to send" chooser for the digest
  const [showDigestDialog, setShowDigestDialog] = useState(false);
  const [digestOption, setDigestOption] = useState('none');
  // iter247 — pending invites (typed phones with no SplitEasy account yet)
  const [waCode, setWaCode] = useState('');
  const [explainMember, setExplainMember] = useState(null);
  const [guests, setGuests] = useState(null);
  const [formerMembers, setFormerMembers] = useState([]);
  const pendingPhones = isAdmin ? (group?.pending_invite_phones || []) : [];

  useEffect(() => {
    if (!group?.id) return;
    api.get(`/groups/${group.id}/guests`).then((r) => setGuests(r.data)).catch(() => setGuests(null));
    if (isAdmin) {
      api.get(`/groups/${group.id}/former-members`)
        .then((r) => setFormerMembers(r.data.former_members || []))
        .catch(() => setFormerMembers([]));
    }
  }, [group?.id, isAdmin]);

  const refreshGuests = async () => {
    try {
      const r = await api.get(`/groups/${group.id}/guests`);
      setGuests(r.data);
      fetchGroup?.();
    } catch { /* ignore */ }
  };

  const isCashHolder = user?.id === (fundData?.treasurer_id || group?.treasurer_id) || user?.is_super_admin;
  const [collectFor, setCollectFor] = useState(null);
  const [collectAmt, setCollectAmt] = useState('');
  const [collecting, setCollecting] = useState(false);

  const openCollect = (member, owed) => {
    setCollectFor(member);
    setCollectAmt(owed > 0 ? String(Math.round(owed)) : '');
  };

  const submitCollect = async () => {
    const amt = Number(collectAmt);
    if (!amt || amt <= 0) { toast.error('Enter the cash you received'); return; }
    setCollecting(true);
    try {
      await api.post(`/groups/${group.id}/fund/setup`, {
        total_amount: amt,
        contributor_id: collectFor.id,
        status: 'received',
        note: `Cash collected from ${collectFor.name}`,
      });
      toast.success(`₹${Math.round(amt).toLocaleString('en-IN')} collected from ${collectFor.name}`);
      setCollectFor(null);
      setCollectAmt('');
      fetchGroup?.();
      fetchFund?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not record the cash');
    } finally {
      setCollecting(false);
    }
  };

  const handleMakeGuest = async (member) => {
    if (!window.confirm(`Move ${member.name} out of the members list and keep them as a guest?\n\nTheir money history stays exactly as it is — from now on they're billed as a guest and are not part of the group fund.`)) return;
    try {
      const r = await api.post(`/groups/${group.id}/members/${member.id}/make-guest`);
      toast.success(r.data?.message || `${member.name} is now a guest`);
      fetchGroup?.();
      const g = await api.get(`/groups/${group.id}/guests`);
      setGuests(g.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not make guest');
    }
  };

  const handleWaInvite = async (phone) => {
    try {
      // iter248 — system-sent from SplitEasy's Meta WhatsApp number
      await sendSystemWaInvite(group.id, phone);
      toast.success(`Invite sent to ${phone} from SplitEasy's WhatsApp ✓`);
    } catch {
      try {
        let code = waCode;
        if (!code) {
          const res = await api.post(`/groups/${group.id}/invite`);
          code = res.data?.code;
          setWaCode(code);
        }
        window.open(waInviteUrl(phone, group.name, code), '_blank');
        toast.info('System WhatsApp unavailable — opened your WhatsApp instead');
      } catch {
        toast.error('Could not generate invite link');
      }
    }
  };

  const handleDismissPending = async (phone) => {
    try {
      await api.post(`/groups/${group.id}/pending-invites/dismiss`, { phone });
      fetchGroup?.();
    } catch {
      toast.error('Could not remove');
    }
  };

  return (
    <div data-testid="members-tab-body" className="space-y-3">
      {/* Trim — primary action is "Add Member"; reminder/digest are admin shortcuts.
          Audit Log / Reconcile / Invite Link have moved to the group ⋮ menu sheet. */}
      {isAdmin && (
        <div className="flex flex-wrap gap-2 mb-2" data-testid="members-action-bar">
          <Button
            onClick={() => setShowAddMember(true)}
            data-testid="add-member-btn"
            className="rounded-full text-xs h-9"
          >
            <UserPlus className="w-4 h-4 mr-1.5" />
            Add member
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setInviteCode(''); setShowInviteModal(true); }}
            data-testid="invite-link-btn"
            className="rounded-full text-xs h-9"
          >
            Invite link
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDigestDialog(true)}
            disabled={sendingGroupDigest}
            data-testid="send-group-digest-btn"
            className="rounded-full text-xs h-9 ml-auto"
          >
            <Send className="w-3.5 h-3.5 mr-1" />
            {sendingGroupDigest ? '…' : 'Digest'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleSendReminder()}
            disabled={sendingReminder === 'all'}
            data-testid="remind-all-btn"
            className="rounded-full text-xs h-9"
          >
            <MessageCircle className="w-3.5 h-3.5 mr-1" />
            {sendingReminder === 'all' ? '…' : 'Remind'}
          </Button>
        </div>
      )}

      {/* iter247 — Pending invites: typed numbers that have no account yet */}
      {pendingPhones.length > 0 && (
        <Card className="p-3 rounded-2xl border-2 border-dashed border-amber-500/40 bg-amber-500/5" data-testid="pending-invites-strip">
          <p className="text-xs font-bold text-amber-700 dark:text-amber-400 uppercase tracking-[0.16em] mb-2">
            Pending invites — not on SplitEasy yet
          </p>
          <div className="space-y-2">
            {pendingPhones.map((phone) => (
              <div key={phone} className="flex items-center gap-2" data-testid={`pending-phone-${phone}`}>
                <p className="flex-1 font-semibold text-sm tabular-nums truncate">{phone}</p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setNewMemberPhone?.(phone); setShowAddMember(true); }}
                  data-testid={`pending-add-${phone}`}
                  className="rounded-full h-8 text-xs"
                >
                  <UserPlus className="w-3 h-3 mr-1" /> Add
                </Button>
                <button
                  type="button"
                  onClick={() => handleWaInvite(phone)}
                  data-testid={`pending-wa-${phone}`}
                  title="Send WhatsApp invite"
                  className="w-8 h-8 rounded-full flex items-center justify-center text-green-600 hover:bg-green-500/10 transition"
                >
                  <MessageCircle className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDismissPending(phone)}
                  data-testid={`pending-dismiss-${phone}`}
                  title="Remove"
                  className="w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground hover:bg-rose-500/10 hover:text-rose-600 transition"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            "Add" puts them in the group now (they claim their spot at signup). WhatsApp sends them a join link.
          </p>
        </Card>
      )}

      {group?.members?.map((member, index) => {
        const isMemberAdmin = group.admin_ids?.includes(member.id);
        const balance = fundData?.coin_balances?.[member.id];
        const hasBalance = balance != null;
        const canEditRole   = (isAdmin || user?.is_super_admin) && member.id !== user?.id;
        const canRemove     = isAdmin && member.id !== user?.id && member.id !== group.created_by;
        const canRemind     = isAdmin && balance < 0;
        const canCollect    = isAdmin && member.id !== user?.id;
        const showActionBar = canEditRole || canRemove || canRemind || canCollect;
        return (
          <motion.div
            key={member.id}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.24, delay: Math.min(index, 12) * 0.03, ease: [0.22, 1, 0.36, 1] }}
          >
            <Card className="p-3 border rounded-2xl" data-testid={`member-item-${member.id}`}>
              {/* Primary row */}
              <div className="flex items-center gap-3">
                <Avatar name={member.name} size="md" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="font-semibold tracking-tight truncate">{member.name}</p>
                    {member.is_super_admin && (
                      <span
                        className="text-xs uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-violet-500/15 text-violet-700 dark:text-violet-300 font-bold"
                        data-testid={`super-admin-badge-${member.id}`}
                        title="Super admin — app-wide administrator"
                      >
                        Super Admin
                      </span>
                    )}
                    {isMemberAdmin && (
                      <span className="text-xs uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-primary/15 text-primary font-bold">
                        Admin
                      </span>
                    )}
                    {/* iter129 — ghost / shadow user badge */}
                    {(member.is_shadow || member.status === 'shadow') && (
                      <span
                        className="text-xs uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 font-bold inline-flex items-center gap-0.5"
                        data-testid={`ghost-badge-${member.id}`}
                        title="Pre-added by admin. Auto-merges when they sign up."
                      >
                        Ghost
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground tabular-nums flex items-center gap-2 flex-wrap">
                    <span>{member.phone}</span>
                    {hasBalance && (
                      <BalanceChip
                        amount={balance}
                        size="sm"
                        testId={`member-coins-${member.id}`}
                        onExplain={() => setExplainMember(member)}
                      />
                    )}
                  </p>
                </div>
                {/* iter131 — proof-on-chain chip */}
                <NeoPayProofLink
                  url={member.neopay_proof_url}
                  testId={`neopay-proof-member-${member.id}`}
                  label={`View ${member.name} on NeoPay ledger`}
                  size="sm"
                />
                {(isAdmin || member.id === user?.id) && (
                  <button
                    onClick={() => openMemberLedger(member)}
                    data-testid={`member-ledger-${member.id}`}
                    className="shrink-0 w-8 h-8 rounded-full text-muted-foreground hover:bg-accent hover:text-foreground flex items-center justify-center active:scale-95 transition"
                    title={member.id === user?.id ? "View my ledger" : "View ledger"}
                    aria-label={member.id === user?.id ? "View my ledger" : "View ledger"}
                  >
                    <BookOpen className="w-4 h-4" />
                  </button>
                )}
                {/* iter222 — pair-wise ledger drill-down (with-me view) */}
                {member.id !== user?.id && openPairLedger && (
                  <button
                    onClick={() => openPairLedger(member)}
                    data-testid={`member-pair-ledger-${member.id}`}
                    className="shrink-0 w-8 h-8 rounded-full text-primary hover:bg-primary/10 flex items-center justify-center active:scale-95 transition"
                    title={`View ledger between you and ${member.name}`}
                    aria-label={`View ledger between you and ${member.name}`}
                  >
                    <ArrowLeftRight className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Secondary action row — Remind stays primary; role/remove live in the ⋮ menu */}
              {showActionBar && (
                <div className="flex items-center gap-1.5 mt-2.5 pt-2.5 border-t border-border/40">
                  {canCollect && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openCollect(member, balance < 0 ? -balance : 0)}
                      data-testid={`collect-cash-${member.id}`}
                      className="rounded-full h-8 text-xs text-primary hover:bg-primary/10"
                    >
                      <Coins className="w-3.5 h-3.5 mr-1" />
                      Collect cash
                    </Button>
                  )}
                  {canRemind && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSendReminder(member.id)}
                      disabled={sendingReminder === member.id}
                      data-testid={`remind-member-${member.id}`}
                      className="rounded-full h-8 text-xs text-emerald-600 hover:bg-emerald-500/10"
                    >
                      <MessageCircle className="w-3.5 h-3.5 mr-1" />
                      Remind
                    </Button>
                  )}
                  {(canEditRole || canRemove) && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="rounded-full h-8 text-xs ml-auto text-muted-foreground"
                          data-testid={`member-menu-${member.id}`} aria-label={`More actions for ${member.name}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="rounded-xl">
                        {canEditRole && (
                          <DropdownMenuItem
                            onClick={() => handleToggleGroupAdmin(member.id, isMemberAdmin)}
                            data-testid={`toggle-group-admin-${member.id}`}
                            className="text-xs font-semibold"
                          >
                            {isMemberAdmin
                              ? (<><ShieldOff className="w-3.5 h-3.5 mr-2" />Remove as admin</>)
                              : (<><ShieldPlus className="w-3.5 h-3.5 mr-2" />Make admin</>)}
                          </DropdownMenuItem>
                        )}
                        {canRemove && (
                          <DropdownMenuItem
                            onClick={() => handleMakeGuest(member)}
                            data-testid={`make-guest-${member.id}`}
                            className="text-xs font-semibold text-amber-600 focus:text-amber-600"
                          >
                            <UserMinus className="w-3.5 h-3.5 mr-2" />
                            Make guest-only
                          </DropdownMenuItem>
                        )}
                        {canRemove && (
                          <DropdownMenuItem
                            onClick={() => handleRemoveMember(member.id, member.name)}
                            data-testid={`remove-member-${member.id}`}
                            className="text-xs font-semibold text-rose-600 focus:text-rose-600"
                          >
                            <X className="w-3.5 h-3.5 mr-2" />
                            Remove from group
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              )}
            </Card>
          </motion.div>
        );
      })}

      {/* Former Members — left the group but still have money on the books */}
      {isAdmin && formerMembers.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="former-members-section">
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-[0.16em]">
            Former members
          </p>
          {formerMembers.map((f) => (
            <Card key={f.user_id} className="p-3 rounded-2xl border-2 border-dashed opacity-90"
              data-testid={`former-member-${f.user_id}`}>
              <div className="flex items-center gap-3">
                <Avatar name={f.name} size="md" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{f.name}</p>
                  <p className="text-xs text-muted-foreground tabular-nums truncate">
                    {f.found ? (f.phone || 'no number on file') : `account deleted · id ${f.user_id.slice(0, 8)}`}
                    {f.last_session_date ? ` · last played ${f.last_session_date}` : ''}
                  </p>
                </div>
                <span className={`shrink-0 text-xs font-bold px-2 py-1 rounded-full tabular-nums ${f.owes ? 'bg-rose-500/10 text-rose-600' : 'bg-emerald-500/10 text-emerald-600'}`}
                  data-testid={`former-balance-${f.user_id}`}>
                  {f.owes ? 'owes ' : 'to get '}₹{Math.abs(Math.round(f.balance)).toLocaleString('en-IN')}
                </span>
              </div>
              <Button size="sm" variant="outline" onClick={() => handleAddMemberBack(f.user_id, f.name)}
                data-testid={`add-back-${f.user_id}`} className="rounded-full text-xs h-8 w-full mt-2">
                <UserPlus className="w-3 h-3 mr-1" /> Add back to collect
              </Button>
            </Card>
          ))}
        </div>
      )}

      {/* iter318 — Guests live outside the group: no fund share, cash fees only */}
      {guests?.guests?.length > 0 && (
        <div className="mt-5 space-y-2" data-testid="guests-section">
          <div className="flex items-baseline justify-between">
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-[0.16em]">
              Guests ({guests.count})
            </p>
            {guests.total_pending > 0 && (
              <p className="text-xs font-bold text-rose-600 tabular-nums" data-testid="guests-total-pending">
                ₹{Math.round(guests.total_pending).toLocaleString('en-IN')} to collect
              </p>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Guests are not part of the group — they never share the fund and only pay their own session fee in cash.
          </p>
          {guests.guests.map((g) => (
            <GuestRow key={g.key} g={g} groupId={group.id} isAdmin={isAdmin}
              onChanged={refreshGuests} />
          ))}
        </div>
      )}

      {/* iter322 — Collect cash from a member, straight from their row */}
      <Dialog open={!!collectFor} onOpenChange={(o) => !o && setCollectFor(null)}>
        <DialogContent className="max-w-sm rounded-2xl" data-testid="collect-cash-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              Cash from {collectFor?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Amount received</Label>
            <Input type="number" inputMode="numeric" value={collectAmt} autoFocus
              onChange={(e) => setCollectAmt(e.target.value)} placeholder="0"
              className="h-11 rounded-xl border-2 text-base" data-testid="collect-cash-amount" />
            <p className="text-xs text-muted-foreground">
              Goes into the cash box and clears that much from {collectFor?.name?.split(' ')[0]}&apos;s balance.
            </p>
            {!isCashHolder && (
              <p className="text-xs text-amber-600" data-testid="collect-cash-holder-hint">
                Only the group&apos;s cash holder can take cash in. Hand it to them, or set yourself as cash holder in Money settings.
              </p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setCollectFor(null)} disabled={collecting} data-testid="collect-cash-cancel">
              Cancel
            </Button>
            <Button onClick={submitCollect} disabled={collecting} data-testid="collect-cash-confirm" className="rounded-full">
              {collecting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Received'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter239 — Digest options dialog */}
      <Dialog open={showDigestDialog} onOpenChange={setShowDigestDialog}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="digest-options-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Send className="w-5 h-5 text-primary" /> Send Digest on WhatsApp
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {DIGEST_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setDigestOption(opt.id)}
                data-testid={`digest-option-${opt.id}`}
                className={`w-full text-left p-3 rounded-xl border-2 transition-colors ${
                  digestOption === opt.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent'
                }`}
              >
                <p className="font-semibold text-sm flex items-center gap-1.5">
                  {opt.id !== 'none' && <FileText className="w-4 h-4 text-primary" />}
                  {opt.label}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">{opt.sub}</p>
              </button>
            ))}
            <Button
              className="w-full rounded-full font-bold mt-1"
              disabled={sendingGroupDigest}
              onClick={async () => {
                setShowDigestDialog(false);
                await handleSendGroupDigest(digestOption);
              }}
              data-testid="digest-send-confirm-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {sendingGroupDigest ? 'Sending…' : `Send to ${group?.members?.length || 0} members`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ExplainBalanceSheet
        open={!!explainMember}
        groupId={group?.id}
        member={explainMember || {}}
        onClose={() => setExplainMember(null)}
      />
    </div>
  );
}
